import { useEffect, useRef } from 'react';
import { dashboardApi } from '../../services/dashboardApi';
import { transformPrometheusData, parseGrafanaTimeToUnix } from '../../utils/dataTransformers';
import { replacePanelVariables } from '../../utils/variableReplacer';

const QueryRunner = ({ panel, refreshKey, timeRange, variables = {}, onDataUpdate, onLoading, onError }) => {
  const lastQueryRef = useRef('');

  useEffect(() => {
    const executeQuery = async () => {
      // Validation checks
      if (!panel.targets || panel.targets.length === 0 || !panel.targets[0]?.expr) {
        return;
      }

      const panelWithReplacedVars = replacePanelVariables(panel, variables);
      const target = panelWithReplacedVars.targets[0];
      const query = target.expr;

      if (!query || !query.trim()) {
        return;
      }

      // Check for unresolved variables
      if (query.includes('$')) {
        console.warn('Query has unresolved variables:', query);
        onError('Query contains unresolved variables');
        return;
      }

      // Prevent duplicate queries
      if (lastQueryRef.current === query) {
        return;
      }
      lastQueryRef.current = query;

      onLoading(true);

      try {
        const now = Date.now();
        const start = parseGrafanaTimeToUnix(timeRange.from, now);
        const end = parseGrafanaTimeToUnix(timeRange.to, now);

        const ds = panel.datasource;
        const datasourceId = (typeof ds === "object" && (ds?.id ?? ds?.uid)) || "default";
        const step = target.step || "60s";

        const response = await dashboardApi.executeQuery({
          datasourceId,
          query,
          start,
          end,
          step
        });

        if (response.success && response.data?.result) {
          const transformedData = transformPrometheusData(response.data.result, panel.type);
          onDataUpdate(transformedData);
        } else {
          onError('No data returned from query');
        }
      } catch (error) {
        console.error('Query execution error:', error);
        onError(error.message || 'Failed to execute query');
      } finally {
        onLoading(false);
      }
    };

    executeQuery();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel.id, panel.targets, panel.type, timeRange.from, timeRange.to, variables, refreshKey]);

  return null;
};

export default QueryRunner;